import { useEffect, useState } from 'react';
import { en } from './en.js';
import { bg } from './bg.js';

/**
 * `t('key', params)` over typed dictionaries (§14). Keys are the English dictionary's;
 * another locale overrides a subset and falls back to English for the rest. The locale
 * follows the signed-in user's profile (`User.locale`).
 */
export type Locale = 'en' | 'bg';
export type Key = keyof typeof en;
export type Params = Record<string, string | number>;

const dicts: Record<Locale, Partial<Record<Key, string>>> = { en, bg };
let current: Locale = 'en';
const listeners = new Set<() => void>();

export function getLocale(): Locale {
  return current;
}

export function setLocale(l: Locale): void {
  if (l === current) return;
  current = l;
  for (const fn of listeners) fn();
}

export function t(key: Key, params?: Params): string {
  let s: string = dicts[current][key] ?? en[key];
  if (params) for (const [k, v] of Object.entries(params)) s = s.split(`{${k}}`).join(String(v));
  return s;
}

/** Re-renders the component when the locale changes. */
export function useT(): typeof t {
  const [, bump] = useState(0);
  useEffect(() => {
    const fn = () => bump((n) => n + 1);
    listeners.add(fn);
    return () => {
      listeners.delete(fn);
    };
  }, []);
  return t;
}

/** Log entry type → label, typed so an unknown type still renders. */
export function logTypeLabel(type: string): string {
  const k = `logType.${type}` as Key;
  return k in en ? t(k) : type;
}
